<template>
	<div>
		<!-- This example requires Tailwind CSS v2.0+ -->
		<div class="flex items-center justify-between" :class="{ 'hidden': (hideSelected && localValue === 1) }">
			<span class="flex-grow flex flex-col" id="toggleLabel">
				<div class="flex justify-between w-full items-center">
					<span class="text-base md:text-base font-medium text-gray-900">{{
						label
					}}</span>
					<img :src="getImageSrc(name)" :alt="`${name} icon`" class="opacity-75" />
				</div>
				<span
					class="hidden md:block text-sm leading-normal text-gray-500 mt-1"
					v-if="description" v-html="description"
					></span
				>
			</span>
		</div>
		<div class="block md:hidden pt-2" :class="{ 'hidden': (hideSelected && localValue === 1) }">
			<span class="text-sm leading-normal text-gray-500" v-if="description" v-html="description"></span>
		</div>

		<div v-if="infoBlurb" class="block pt-2 pb-1" style="line-height: 1.2rem;">
			<span class="text-sm text-gray-800">&#x1F6C8;</span> <span class="text-xs text-gray-500 italic">{{
				infoBlurb
			}}</span>
		</div>

		<div class="block pt-5"
			:class="{
				'hidden': (hideSelected && localValue === 1),
				'border-b-2 border-gray-200 pb-6': !hideBottomBorder
			}">
			<span class="relative z-0 flex flex-col sm:flex-row gap-4 pb-2">
				<!-- Desktop view for buttons, No left & Yes Right -->
				<button
					v-for="(option, key) in sortedOptionDesktop"
					:key="`${name}-desktop-${option.value}`"
					type="button"
					:dusk="option.dusk"
					class="relative w-full rounded-md hidden sm:flex text-gray-600 items-center justify-center sm:px-4 py-3 border-2 text-sm font-medium focus:z-10"
					@click="select(option)"
					:class="{
						'-ml-px': key,
						'border-green-400': isSelected(option) && option.value === 1,
						'border-red-400': isSelected(option) && option.value === 0,
						'border-gray-300 bg-white  hover:bg-gray-50': !isSelected(option),
					}"
					:aria-pressed="isSelected(option)"
				>

				<span
					class="rounded-full flex items-center justify-center sm:w-6 sm:h-6 w-5 h-5"
					:class="{
						'bg-green-400 text-white': isSelected(option) && option.value === 1,
						'bg-red-400 text-white': isSelected(option) && option.value === 0,
						'border-gray-300 border-2 bg-white  hover:bg-gray-50 text-transparent': !isSelected(option),
					}"

					>
					<img v-if="isSelected(option) && option.value === 0 || isSelected(option) && option.value === 1" class="text-white w-4/6 h-4/6" :src="option.value === 1 ? getImageSrc('tick') : getImageSrc('cross') " alt="status-icon" />
				</span>
				<span class="ml-2 xs:ml-2 lg:ml-3">
					{{ option.value === 0 ? "No, I'll risk it" :  cleanPrice(servicePriceObject) }}
				</span>
				</button>
				<!-- Desktop view for buttons, No Bottom & Yes Top -->
				<button
					v-for="(option, key) in sortedOptionMobile"
					:key="`${name}-mobile-${option.value}`"
					type="button"
					:dusk="option.dusk"
					class="relative w-full rounded-md flex sm:hidden text-gray-600 items-center justify-center px-2 xs:px-3 py-3 border-2 text-sm font-medium focus:z-10"
					@click="select(option)"
					:class="{
						'-ml-px': key,
						'border-green-400': isSelected(option) && option.value === 1,
						'border-red-400': isSelected(option) && option.value === 0,
						'border-gray-300 bg-white  hover:bg-gray-50': !isSelected(option),
					}"
					:aria-pressed="isSelected(option)"
				>

				<span
					class="rounded-full flex items-center justify-center sm:w-6 sm:h-6 w-5 h-5"
					:class="{
						'bg-green-400 text-white': isSelected(option) && option.value === 1,
						'bg-red-400 text-white': isSelected(option) && option.value === 0,
						'border-gray-300 border-2 bg-white  hover:bg-gray-50 text-transparent': !isSelected(option),
					}"

					>
					<img v-if="isSelected(option) && option.value === 0 || isSelected(option) && option.value === 1" class="text-white w-4/6 h-4/6" :src="option.value === 1 ? getImageSrc('tick') : getImageSrc('cross') " alt="status-icon" />
				</span>
				<span class="ml-2 xs:ml-2 lg:ml-3">
					{{ option.value === 0 ? "No, I'll risk it" :  cleanPrice(servicePriceObject) }}
				</span>
				</button>
			</span>
			<span v-if="filteredErrors.includes(name)" class="text-sm text-red-500">
	            Please select if you'd like {{ (label) }}.
			</span>
		</div>
	</div>
</template>

<script>
export default {
	name: "ButtonGroup",
	computed: {
		localValue: {
			get() {
				return this.value;
			},
			set(localValue) {
				this.$emit("input", localValue);
			},
		},
		sortedOptionDesktop() {
			return [...this.options].sort((a, b) =>
				a.value === 0 ? -1 : b.value === 0 ? 1 : 0
			);
		},
		sortedOptionMobile() {
			return [...this.options].sort((a, b) =>
				a.value === 0 ? 1 : b.value === 0 ? -1 : 0
			);
		},
		filteredErrors() {
			return this.errorsArray.filter(item => item === this.name);
		},
	},
	props: {
		hideBottomBorder: {
			type: Boolean,
			default: true,
		},
		options: {
			type: Array,
			default: () => [],
		},
		value: {
			type: [String, Number],
			default: null,
		},
		name: {
			type: String,
			default: null,
		},
		label: {
			type: String,
			default: "",
		},
		description: {
			type: String,
			default: null,
		},
		hideSelected: {
			type: Boolean,
			default: false,
		},
		infoBlurb: {
			type: String,
			default: null,
		},
		servicePriceObject: {
			type: Array,
			default: () => [],
		},
		errorsArray: {
			type: Array,
			default: () => [],
		},
		indexValue: {
			type: Number,
			default: 0,
		}
	},
	methods: {
		isSelected(option) {
			return option.value === this.localValue;
		},
		select(option) {
			if(this.isSelected(option)) {
				return;
			}

			let runCalculatePrice = false;

			// skip price calculation if select no I'll risk it if originally not selected and not changing from selected to not selected
			if(!option.value && !this.localValue) {
				runCalculatePrice = true;
			}

			this.localValue = option.value;
	
			if(!runCalculatePrice) {
				this.$emit("change");
			}
	
			// on service selection, removes the service from errors array so does not appear anymore
			const errorIndex = this.errorsArray.indexOf(this.name);
			if (errorIndex !== -1) {
				this.errorsArray.splice(errorIndex, 1);
			}
		},
		getImageSrc(iconName) {
			return `/img/icons/svg/${iconName}.svg`;
		},
		cleanPrice(servicePriceArray) {
			if (servicePriceArray[0] && servicePriceArray[0].price_for_quote) {
				let price = 0
					let formattedPrice = ""
				if(this.isBusinessFlow()) {
					price = this.getVatExclusivePrice(servicePriceArray[0].price_for_quote) / 100
				} else {
					price = servicePriceArray[0].price_for_quote / 100;
				}
				price < 1 ? formattedPrice = `${price * 100}p`
				: price % 1 === 0 ?
					formattedPrice = new Intl.NumberFormat("en-GB", {
						style: "currency",
						currency: "GBP",
						minimumFractionDigits: 0,
						maximumFractionDigits: 0,
					}).format(price)
				: formattedPrice = new Intl.NumberFormat("en-GB", {
						style: "currency",
						currency: "GBP",
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}).format(price);

				return `Yes, add for ${formattedPrice}`;
			}
		},

    },
};

</script>

<style scoped></style>
